import React from "react";

/**
 * Iter 234 (Aug 2026 · spec §1E) · Committed-time gate banner.
 * =============================================================
 *
 * Renders when a committed callback slot is inside the 5-minute
 * window. Parent (`SalesDialerLive`) owns the gate state and the
 * two handler callbacks; this component is stateless.
 *
 * Props:
 *   gate         { leadId, leadName, minutesUntil, secondsUntil }
 *   onDialNow()  jump to committed lead + start call immediately
 *   onDismiss()  clear the gate (queue auto-advance resumes)
 *
 * Visual escalation: amber background >60s, red-tone <=60s.
 */
export function CommittedCallGateBanner({ gate, onDialNow, onDismiss }) {
  const { leadName, secondsUntil } = gate || {};
  const mm = Math.floor((secondsUntil || 0) / 60);
  const ss = Math.max(0, (secondsUntil || 0) - mm * 60);
  const countdown = `${mm}:${ss.toString().padStart(2, "0")}`;
  const urgent = (secondsUntil || 0) <= 60;
  return (
    <div
      data-testid="committed-call-gate-banner"
      data-urgent={urgent ? "true" : "false"}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        padding: "10px 20px",
        background: urgent
          ? "linear-gradient(90deg, #7F1D1D 0%, #B91C1C 100%)"
          : "linear-gradient(90deg, #92400E 0%, #B45309 100%)",
        color: "#FEF3C7",
        borderBottom: "1px solid rgba(0,0,0,0.15)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        fontSize: 13,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        <span
          data-testid="committed-call-gate-countdown"
          style={{ fontWeight: 700, fontSize: 15, letterSpacing: "0.02em", fontVariantNumeric: "tabular-nums", background: "rgba(0,0,0,0.25)", padding: "3px 10px", borderRadius: 6 }}
        >
          {countdown}
        </span>
        <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          Committed call approaching — <span data-testid="committed-call-gate-name">{leadName}</span>
        </span>
        <span style={{ opacity: 0.85, fontSize: 12 }}>
          · Auto-dial paused
        </span>
      </div>
      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <button
          data-testid="committed-call-gate-dial-now"
          onClick={onDialNow}
          style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.4)", background: "#FEF3C7", color: "#7F1D1D", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
        >
          Dial now
        </button>
        <button
          data-testid="committed-call-gate-dismiss"
          onClick={onDismiss}
          style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.35)", background: "transparent", color: "#FEF3C7", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

export default CommittedCallGateBanner;
